import api from '../api';
import type { SearchCount } from './searchCount';

// ── Типы ──────────────────────────────────────────────────────────────────────

export interface LotListItem {
  id: number;
  lotNumber: string | null;
  nameRu: string | null;
  nameKz: string | null;
  descriptionRu: string | null;
  descriptionKz: string | null;
  customerNameRu: string | null;
  customerNameKz: string | null;
  customerBin: string | null;
  amount: number | null;
  count: number | null;
  trdBuyId: number | null;
  trdBuyNumberAnno: string | null;
  refLotStatusId: number | null;
  refTradeMethodsId: number | null;
  refBuyTradeMethodsId?: number | null;
  enstruCode: string | string[] | null;
  kpvedSection: string | string[] | null;
  indexDate: string | null;
  plnPointKatoList: string[] | null;
  announcement_status?: NamedRef | null;
  announcement_revision?: AnnouncementRevision | null;
}

export interface LotSearchResponse extends SearchCount {
  items: LotListItem[];
  next_cursor?: string | null;
  search_session?: string | null;
  pagination_mode?: 'cursor';
  has_more?: boolean;
  sort_degraded?: boolean; // сортировка не уложилась в тайм-аут — выдача без неё
}

export interface RefItem {
  id: number;
  name_ru: string | null;
  name_kz: string | null;
}

export interface RegionItem {
  code: string;
  name_ru: string | null;
  name_kz: string | null;
}

export interface LotRefsResponse {
  statuses: RefItem[];
  trade_methods: RefItem[];
  regions: RegionItem[];
  subject_types?: RefItem[];
  subject_type_filter_ready?: boolean;
}

export type ProcurementSubjectType = 1 | 2 | 3;

export interface CategorySection {
  section: string;
  name_ru: string;
  name_kz?: string | null;
  codes: number;
  lots: number;
}

export type CategoryGroup = 'goods' | 'works' | 'services';

export type CategoryTree = Record<CategoryGroup, CategorySection[]>;

export interface CategoryCode {
  code: string;
  nameRu: string | null;
  nameKz: string | null;
  descRu?: string | null; // характеристика — различает одноимённые коды
  descKz?: string | null;
  lots: number;
  kpvedSection: string;
  kpvedSectionName?: string | null;
  kpvedSectionNameKz?: string | null;
}

export interface NamedRef {
  id: number;
  name_ru: string | null;
  name_kz: string | null;
}

export interface LotAppItem {
  id: number;
  supplierBinIin: string | null;
  supplierNameRu?: string | null;
  supplierNameKz?: string | null;
  protNumber: string | null;
  dateApply: string | null;
  lotPrice: number | null;
  lotAmount: number | null;
  lotsCount: number;
  lot_membership_known?: boolean;
}

export interface AnnouncementRevision {
  number: string | null;
  revision: number | null;
  is_latest: boolean | null;
  latest: {
    id: number;
    number: string;
    status: NamedRef | null;
    portal_url: string;
    goszakup_url: string;
  } | null;
  complete: boolean;
}

export interface LotDocument {
  id?: number | string;
  name_ru?: string | null;
  name_kz?: string | null;
  url: string;
  url_ru?: string | null;
  url_kz?: string | null;
  number?: string | null;
  kind?: 'results' | 'discussion' | 'opening' | 'document';
  lot_id?: number | null;
}

export interface LotDetailResponse {
  lot: Record<string, any> & Partial<Pick<LotListItem, 'enstruCode' | 'kpvedSection'>>;
  lot_status: NamedRef | null;
  trade_method: NamedRef | null;
  trd_buy: Record<string, any> | null;
  buy_status: NamedRef | null;
  subject_type: NamedRef | null;
  anno_lots: LotListItem[];
  apps: LotAppItem[];
  plan_points: Record<string, any>[];
  contracts: Record<string, any>[];
  enstru: { code: string; nameRu: string | null; nameKz: string | null; descRu?: string | null; descKz?: string | null; kpvedSectionName: string | null; kpvedSectionNameKz?: string | null } | null;
  goszakup_url: string | null;
  documents?: LotDocument[];
  protocols?: LotDocument[];
  documents_partial?: boolean;
  announcement_status?: NamedRef | null;
  announcement_revision?: AnnouncementRevision | null;
}

export interface SupplierInsight {
  bin: string;
  name_ru?: string | null;
  name_kz?: string | null;
  participations: number | null;
  wins: number | null;
  won_sum: number | null;
  in_rnu?: boolean;
}

export interface LotSuppliersResponse {
  items: SupplierInsight[];
  total: number;
  available: boolean;
  reason?: string;
  partial?: boolean;
  metric_unit?: string;
  metric_scope?: 'supplier_history';
  discovery_partial?: boolean;
  metrics_partial?: boolean;
}

export type LotSupplierSort = 'supplier' | 'participations' | 'wins' | 'won_sum';

export interface LotSupplierParams {
  skip?: number;
  limit?: number;
  q?: string;
  sort?: LotSupplierSort;
  order?: 'asc' | 'desc';
  language?: 'ru' | 'kk';
}

export interface LotInsightsResponse {
  available: boolean;
  reason?: string;
  enstru_code?: string;
  price_stats?: {
    sample: number;
    avg_amount: number | null;
    median_amount: number | null;
    min_amount: number | null;
    max_amount: number | null;
    median_unit_price: number | null;
  };
  competition?: {
    similar_lots: number;
    applications_sampled: number;
    avg_applications_per_buy: number | null;
    unique_participants: number;
  };
  suppliers_total?: number;
}

export interface LotSearchParams {
  q?: string;
  lot_number?: string;
  anno_number?: string;
  customer_bin?: string;
  customer_name?: string;
  status?: number[];
  trade_method?: number[];
  subject_type?: ProcurementSubjectType;
  enstru_code?: string[];
  kpved_section?: string;
  group?: CategoryGroup; // вместе с kpved_section: только коды группы товары/работы/услуги
  amount_from?: number;
  amount_to?: number;
  region?: string;
  date_from?: string; // YYYY-MM-DD
  date_to?: string;   // YYYY-MM-DD
  sort?: 'newest' | 'amount_desc' | 'amount_asc' | 'relevance';
  skip?: number;
  cursor?: string;
  limit?: number;
}

// ── API ───────────────────────────────────────────────────────────────────────

export const searchLots = async (params: LotSearchParams, signal?: AbortSignal): Promise<LotSearchResponse> => {
  const { data } = await api.get<LotSearchResponse>('/lots/search', {
    params,
    signal,
    timeout: 20000,
    paramsSerializer: { indexes: null }, // status=1&status=2 вместо status[]=
  });
  return data;
};

export const getLotRefs = async (): Promise<LotRefsResponse> => {
  const { data } = await api.get<LotRefsResponse>('/lots/refs');
  return data;
};

export const getCategoryTree = async (signal?: AbortSignal): Promise<CategoryTree> => {
  const { data } = await api.get<CategoryTree>('/lots/categories/tree', { signal });
  return data;
};

export const searchCategoryCodes = async (params: {
  group?: CategoryGroup;
  section?: string;
  search?: string;
  skip?: number;
  limit?: number;
}, signal?: AbortSignal): Promise<{ items: CategoryCode[]; total: number }> => {
  const { data } = await api.get('/lots/categories/codes', { params, signal });
  return data;
};

export const getLotDetail = async (id: number): Promise<LotDetailResponse> => {
  const { data } = await api.get<LotDetailResponse>(`/lots/${id}`);
  return data;
};

export const getLotInsights = async (id: number): Promise<LotInsightsResponse> => {
  const { data } = await api.get<LotInsightsResponse>(`/lots/${id}/insights`);
  return data;
};

export const getLotSuppliers = async (
  id: number,
  params: LotSupplierParams,
  signal?: AbortSignal,
): Promise<LotSuppliersResponse> => {
  const { data } = await api.get<LotSuppliersResponse>(`/lots/${id}/suppliers`, { params, signal });
  return data;
};

// ── Помощники форматирования ──────────────────────────────────────────────────

export const fmtAmount = (v: number | null | undefined): string =>
  v == null ? '—' : new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(v);

export const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '—';
  const normalized = v.replace(' ', 'T');
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString('ru-RU');
};

/** Цветовая семантика статусов лота (по группам id ref_lots_status). */
export const lotStatusTone = (id: number | null | undefined): 'green' | 'blue' | 'gray' | 'red' => {
  if (id == null) return 'gray';
  if ([210, 220, 230, 240, 110].includes(id)) return 'green'; // опубликован / приём заявок
  if ([250, 260, 270, 280, 310, 320, 330, 340].includes(id)) return 'blue'; // рассмотрение/итоги
  if ([430, 440, 450].includes(id)) return 'red'; // отменён/отклонён
  return 'gray';
};
